// TipSummary.tsx
import React, { useState, useEffect } from 'react';
import { Button, Form, InputGroup, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { formatCurrency } from '../../../../../../../../../../utils/functions';
import { FaTrash, FaProjectDiagram } from 'react-icons/fa';
import './TipSummary.scss';


interface TipSummaryProps {
  tipSummary: any | null;
  handleReportedAmountChange: any;
  userReportedData: any;
  setTipDifference: any;
  onFinalTipSummaryChange: (finalTips: any) => void;
  showBalances: boolean;
  missingInputs?: string[];
}

const TipSummary: React.FC<TipSummaryProps> = ({
  tipSummary,
  handleReportedAmountChange,
  userReportedData,
  setTipDifference,
  onFinalTipSummaryChange,
  showBalances,
  missingInputs = [],
}) => {
  const [branchedTags, setBranchedTags] = useState<{ [key: string]: { tag: string; amount: number }[] }>({});
  
  // Validate and sanitize numeric input
  const handleNumericInput = (value: string): number | '' => {
    if (value === '' || value === '-') return '';
    // Allow only numbers, one decimal point, and optional negative sign
    const sanitized = value.replace(/[^0-9.-]/g, '');
    // Prevent multiple decimal points
    const parts = sanitized.split('.');
    const cleaned = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : sanitized;
    // Prevent multiple negative signs and ensure it's only at the start
    const final = cleaned.replace(/(?!^)-/g, '').replace(/^-+/, '-');
    const num = parseFloat(final);
    return isNaN(num) ? '' : num;
  };
  
  const calculateDifference = (actual: number, reported: number) => reported - actual;

  const handleAddTag = (item: string) => {
    const updatedTags = branchedTags[item] || [];
    setBranchedTags({
      ...branchedTags,
      [item]: [...updatedTags, { tag: '', amount: 0 }],
    });
  };

  const handleRemoveTag = (item: string, index: number) => {
    // Clonamos array y quitamos el tag
    const updatedTags = [...(branchedTags[item] || [])];
    updatedTags.splice(index, 1);

    if (updatedTags.length === 0) {
      // Si ya no quedan tags, eliminamos la entrada y notificamos 0
      const newBranched = { ...branchedTags };
      delete newBranched[item];
      setBranchedTags(newBranched);
      handleReportedAmountChange(`item_${item}`, 0);
    } else {
      // Si aún quedan, actualizamos estado y reevaluamos suma
      setBranchedTags({ ...branchedTags, [item]: updatedTags });
      const newSum = updatedTags.reduce((sum, t) => sum + (t.amount || 0), 0);
      handleReportedAmountChange(`item_${item}`, newSum);
    }
  };


  const handleTagChange = (item: string, index: number, key: 'tag' | 'amount', value: string | number) => {
    const updatedTags = [...(branchedTags[item] || [])];
    // Map 'amount' to the actual property name
    if (key === 'amount') {
      updatedTags[index] = { ...updatedTags[index], amount: value as number };
    } else {
      updatedTags[index] = { ...updatedTags[index], tag: value as string };
    }
    setBranchedTags({ ...branchedTags, [item]: updatedTags });
    const reportedAmount = updatedTags.reduce((acc, tag) => acc + (tag.amount || 0), 0);
    handleReportedAmountChange(`item_${item}`, reportedAmount);
  };


  // Report final tip summary including tags
  useEffect(() => {
    if (tipSummary) {
      const finalTips = Object.entries(tipSummary.tips).reduce((acc, [item, tip]: [string, any]) => {
        const reported = userReportedData[`item_${item}`] || 0;
        const tags = branchedTags[item] || [];
        acc[item] = {
          name: item,
          system: tip.amount,
          reported,
          matched: Math.abs(tip.amount - reported) < (tip.amount * 0.0001),
          tags,
        };
        return acc;
      }, {} as any);
      onFinalTipSummaryChange(finalTips);
    }
  }, [tipSummary, branchedTags, userReportedData, onFinalTipSummaryChange]);

  // Update tip difference
  useEffect(() => {
    if (tipSummary) {
      const totalDifference = Object.entries(tipSummary.tips).reduce((acc, [item, tip]: [string, any]) => {
        const reported = userReportedData[`item_${item}`] || 0;
        const difference = calculateDifference(tip.amount, reported);
        return acc + (Math.abs(difference) < tip.amount * 0.0001 ? 0 : difference);
      }, 0);
      setTipDifference(totalDifference);
    }
  }, [tipSummary, userReportedData, branchedTags, setTipDifference]);

  // Don't render the component at all if balances are hidden
  if (!showBalances) {
    return null;
  }

  return (
    <div className="tip-summary-section">
      <div className='tip-summary-section-container-title-toolstips'>
        <h5>Propinas</h5>
      </div>

      <div className="tip-methods-container">
        {tipSummary &&
          Object.entries(tipSummary.tips).map(([item, tip]: [string, any]) => {
            const reported = userReportedData[`item_${item}`] || '';
            const difference = calculateDifference(tip.amount, reported);
            const EPS = 0.01;
            const displayDiff = Math.abs(difference) < EPS ? 0 : difference;
            
            return (
              <div key={item} className="tip-method-card">
                <div className="tip-method-content">
                  <div className="tip-type-column">
                    <div className="tip-type">
                      {item === 'Propina_Efectivo' ? 'Propinas Efectivo' : 
                       item === 'Propina_Tarjeta' ? 'Propinas Tarjeta/Otros' : 
                       item}
                    </div>
                    {tip.description && (
                      <div className="tip-description">{tip.description}</div>
                    )}
                  </div>
                  
                  <div className="system-amount">
                    <div className="amount-label">Sistema</div>
                    <div className="amount-value">
                      {!showBalances ? (
                        <span className="blur-balance">***</span>
                      ) : (
                        formatCurrency(tip.amount)
                      )}
                    </div>
                  </div>
                  
                  <div className="input-section">
                    <InputGroup className="amount-input-group">
                      <InputGroup.Text>$</InputGroup.Text>
                      <Form.Control
                        type="text"
                        className={`amount-input ${missingInputs.includes(`item_${item}`) ? 'input-missing' : ''}`}
                        placeholder="Monto"
                        value={userReportedData[`item_${item}`] ?? ''}
                        onChange={(e) => {
                          const validatedValue = handleNumericInput(e.target.value);
                          handleReportedAmountChange(`item_${item}`, validatedValue);
                        }}
                        disabled={branchedTags[item]?.length > 0}
                      />
                    </InputGroup>
                    {showBalances && (
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>Igualar al sistema</Tooltip>}
                      >
                        <Button
                          variant="none"
                          className="mp-button light equal-button"
                          onClick={() => handleReportedAmountChange(`item_${item}`, tip.amount)}
                          disabled={branchedTags[item]?.length > 0}
                          title="Igualar al sistema"
                        >
                          =
                        </Button>
                      </OverlayTrigger>
                    )}
                  </div>
                  
                  <div className="difference-section">
                    <div className="difference-label">Diferencia</div>
                    <div className={`difference-value ${!showBalances ? '' : (Math.abs(displayDiff) < EPS ? 'difference-green' : (displayDiff > 0 ? 'difference-green' : 'difference-red'))}`}>
                      {!showBalances ? (
                        <span className="blur-balance">***</span>
                      ) : (
                        formatCurrency(displayDiff)
                      )}
                    </div>
                  </div>
                  
                  <div className="tip-actions">
                    <Button
                      variant="none"
                      className="mp-button light"
                      onClick={() => handleAddTag(item)}
                      title="Agregar detalle"
                    >
                      <FaProjectDiagram />
                    </Button>
                  </div>
                </div>

                {branchedTags[item] && branchedTags[item].length > 0 && (
                  <div className="tag-section" onClick={(e) => e.stopPropagation()}>
                    {branchedTags[item].map((tag, index) => (
                      <div key={`${item}_tag_${index}`} className="tag-item">
                        <Form.Control
                          type="text"
                          className="tag-input"
                          placeholder="Etiqueta"
                          value={tag.tag}
                          onChange={(e) => handleTagChange(item, index, 'tag', e.target.value)}
                        />
                        <div className="tag-amount-input">
                          <InputGroup className="amount-input-group">
                            <InputGroup.Text>$</InputGroup.Text>
                            <Form.Control
                              type="text"
                              placeholder="Monto"
                              className="amount-input"
                              value={tag.amount}
                              onChange={(e) => {
                                const validatedValue = handleNumericInput(e.target.value);
                                handleTagChange(item, index, 'amount', validatedValue);
                              }}
                            />
                          </InputGroup>
                        </div>
                        <button
                          className="tag-remove-btn"
                          onClick={() => handleRemoveTag(item, index)}
                          title="Eliminar etiqueta"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default TipSummary;
